import { useState, useRef, useEffect } from 'react'
import { Pencil, Trash2, Check, X, Loader2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import type { ImageRead } from '@/types/image'

interface ImageCardProps {
  image: ImageRead
  onUpdate: (
    imageId: string,
    data: { name?: string; description?: string }
  ) => void
  onDelete: (imageId: string) => void
  isUpdating?: boolean
}

export function ImageCard({
  image,
  onUpdate,
  onDelete,
  isUpdating,
}: ImageCardProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isEditing) inputRef.current?.focus()
  }, [isEditing])

  const startEdit = () => {
    setIsEditing(true)
    setEditName(image.name)
    setEditDescription(image.description ?? '')
  }

  const saveEdit = () => {
    const trimmedName = editName.trim()
    const trimmedDesc = editDescription.trim()
    if (!trimmedName) {
      cancelEdit()
      return
    }
    onUpdate(image.id, {
      name: trimmedName,
      description: trimmedDesc || undefined,
    })
    setIsEditing(false)
  }

  const cancelEdit = () => {
    setIsEditing(false)
    setEditName('')
    setEditDescription('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') cancelEdit()
  }

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    handleKeyDown(e)
    if (e.key === 'Enter') {
      e.preventDefault()
      saveEdit()
    }
  }

  const [imgLoaded, setImgLoaded] = useState(false)
  const imageSrc = image.download_url

  return (
    <div className="group relative rounded-lg border border-border bg-card overflow-hidden">
      {/* Image */}
      <div className="relative aspect-square bg-muted">
        {imageSrc ? (
          <>
            {!imgLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
            <img
              src={imageSrc}
              alt={image.name}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
              onLoad={() => setImgLoaded(true)}
            />
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground text-xs">
            No preview
          </div>
        )}

        {/* Hover Actions */}
        <div className="absolute inset-0 flex items-start justify-end gap-1 p-2 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-b from-black/30 to-transparent">
          <button
            onClick={startEdit}
            className="flex h-7 w-7 items-center justify-center rounded-md bg-white/90 hover:bg-white cursor-pointer"
          >
            <Pencil className="h-3.5 w-3.5 text-foreground" />
          </button>
          <button
            onClick={() => onDelete(image.id)}
            className="flex h-7 w-7 items-center justify-center rounded-md bg-white/90 hover:bg-white cursor-pointer"
          >
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 space-y-1.5">
        {isEditing ? (
          <div className="flex flex-col gap-1.5">
            <Input
              ref={inputRef}
              value={editName}
              onChange={e => setEditName(e.target.value)}
              onKeyDown={handleNameKeyDown}
              placeholder="Image name"
              className="h-7 text-sm"
              disabled={isUpdating}
            />
            <Textarea
              value={editDescription}
              onChange={e => setEditDescription(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={2}
              placeholder="Description (optional)"
              className="text-xs resize-none"
              disabled={isUpdating}
            />
            <div className="flex gap-1 justify-end">
              <button onClick={saveEdit} className="cursor-pointer">
                <Check className="h-4 w-4 text-green-600" />
              </button>
              <button onClick={cancelEdit} className="cursor-pointer">
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          </div>
        ) : (
          <>
            <p
              className="text-sm font-medium text-foreground truncate"
              title={image.name}
            >
              {image.name}
            </p>
            <p
              className="text-xs text-muted-foreground truncate"
              title={image.description ?? ''}
            >
              {image.description || 'No description'}
            </p>
          </>
        )}
      </div>
    </div>
  )
}
